import * as preact from "preact";

import { type PreactPageProps } from "pages/PreactPage";
import readlangClient, { requestErrorMessage } from "readlangClient";

type PlanBreakdown = {
  label: string;
  revenue: number;
  users: number;
};

// Payments in a currency other than US dollars, which the USD totals leave
// out as there's no exchange rate to convert them with
type OtherCurrencyRevenue = {
  currency: string; // lowercase ISO 4217
  revenue: number;
  refunded: number;
  users: number;
};

type MonthRevenue = {
  month: string;
  // US dollars only; gross, so net = revenue - refunded
  revenue: number;
  refunded: number;
  users: number;
  plans: PlanBreakdown[];
  otherCurrencies: OtherCurrencyRevenue[];
};

type State = {
  months?: MonthRevenue[];
  loading: boolean;
  error?: string;
  hoverIndex?: number;
  showRefunded: boolean;
};

const CHART_WIDTH = 900;
const CHART_HEIGHT = 360;
const PADDING_LEFT = 70;
const PADDING_RIGHT = 20;
const PADDING_TOP = 20;
const PADDING_BOTTOM = 40;

const BAR_COLOR = "#4a90d9";
const BAR_HOVER_COLOR = "#1f5f9e";
// Mirror $red / $red-dark1 from css/_constants.scss (SCSS vars aren't
// available to SVG fill attributes).
const REFUND_COLOR = "#d34d2f";
const REFUND_HOVER_COLOR = "#ad3d25";

const formatMoney = (
  value: number,
  currency = "usd",
  maximumFractionDigits = 2
) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits,
  });

// "2024-03" → "March 2024"
const formatMonth = (month: string) => {
  const [year, monthNumber] = month.split("-").map(Number);
  return new Date(Date.UTC(year, monthNumber - 1)).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
};

// Smallest 1/1.5/2/2.5/3/4/5/7.5/10 × 10^n at or above value, for a tidy
// y-axis maximum that doesn't leave most of the chart empty.
const niceCeil = (value: number) => {
  const magnitude = Math.pow(10, Math.floor(Math.log10(Math.max(1, value))));
  for (const multiple of [1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10]) {
    if (magnitude * multiple >= value) return magnitude * multiple;
  }
  return magnitude * 10;
};

export default class AdminRevenuePage extends preact.Component<
  PreactPageProps,
  State
> {
  private mounted = false;

  constructor(props: PreactPageProps) {
    super(props);
    this.state = { loading: true, showRefunded: true };
  }

  async componentDidMount() {
    this.mounted = true;
    try {
      const data = (
        await readlangClient.request("GET", "/api/admin/revenue/monthly", {})
      ).data as { months: MonthRevenue[] };
      if (!this.mounted) return;
      this.setState({ months: data.months, loading: false });
    } catch (err) {
      if (!this.mounted) return;
      this.setState({ error: requestErrorMessage(err), loading: false });
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  renderHoverDetail(month: MonthRevenue) {
    return (
      <div class="revenueHoverDetail">
        <h3>{formatMonth(month.month)}</h3>
        <table class="revenueBreakdownTable">
          <thead>
            <tr>
              <th>Plan</th>
              <th>Revenue</th>
              <th>Users</th>
            </tr>
          </thead>
          <tbody>
            {month.plans.map((plan) => (
              <tr>
                <td>{plan.label}</td>
                <td>{formatMoney(plan.revenue)}</td>
                <td>{plan.users}</td>
              </tr>
            ))}
            <tr class="totalsRow">
              <td>Gross</td>
              <td>{formatMoney(month.revenue)}</td>
              <td>{month.users}</td>
            </tr>
            <tr class="refundsRow">
              <td>Refunds</td>
              <td>−{formatMoney(month.refunded)}</td>
              <td></td>
            </tr>
            <tr class="netRow">
              <td>Net</td>
              <td>{formatMoney(month.revenue - month.refunded)}</td>
              <td></td>
            </tr>
            {month.otherCurrencies.map((other) => (
              <tr class="otherCurrencyRow">
                <td>{other.currency.toUpperCase()}, not in the totals</td>
                <td>
                  {formatMoney(other.revenue, other.currency)}
                  {other.refunded > 0 &&
                    ` (−${formatMoney(other.refunded, other.currency)} refunded)`}
                </td>
                <td>{other.users}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  renderChart() {
    const months = this.state.months;
    if (!months || months.length === 0) {
      return <p>No payments found.</p>;
    }

    const plotWidth = CHART_WIDTH - PADDING_LEFT - PADDING_RIGHT;
    const plotHeight = CHART_HEIGHT - PADDING_TOP - PADDING_BOTTOM;
    const barSlot = plotWidth / months.length;

    const yMax = niceCeil(Math.max(...months.map((month) => month.revenue)));
    const yFor = (revenue: number) =>
      PADDING_TOP + plotHeight - (revenue / yMax) * plotHeight;

    const yTickCount = 5;
    const yTicks: number[] = [];
    for (let index = 0; index <= yTickCount; index++) {
      yTicks.push((yMax * index) / yTickCount);
    }

    // Tick each January; thin to every other year when the range is long.
    const yearStep = months.length > 120 ? 2 : 1;
    const xTicks = months
      .map((month, index) => ({ month: month.month, index }))
      .filter(
        ({ month }) =>
          month.endsWith("-01") && Number(month.slice(0, 4)) % yearStep === 0
      );

    const hoverIndex = this.state.hoverIndex;
    const hoverMonth =
      hoverIndex !== undefined ? months[hoverIndex] : undefined;

    return (
      <div class="revenueChart">
        <svg
          width={CHART_WIDTH}
          height={CHART_HEIGHT}
          viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
          onMouseLeave={() => this.setState({ hoverIndex: undefined })}
          onMouseMove={(event) => {
            const rect = event.currentTarget.getBoundingClientRect();
            const xInSvg =
              ((event.clientX - rect.left) * CHART_WIDTH) / rect.width;
            const index = Math.max(
              0,
              Math.min(
                months.length - 1,
                Math.floor((xInSvg - PADDING_LEFT) / barSlot)
              )
            );
            if (index !== this.state.hoverIndex) {
              this.setState({ hoverIndex: index });
            }
          }}
        >
          {yTicks.map((tick) => (
            <g>
              <line
                x1={PADDING_LEFT}
                x2={CHART_WIDTH - PADDING_RIGHT}
                y1={yFor(tick)}
                y2={yFor(tick)}
                stroke="#eee"
              />
              <text
                x={PADDING_LEFT - 8}
                y={yFor(tick) + 4}
                text-anchor="end"
                font-size="11"
                fill="#666"
              >
                {formatMoney(tick, "usd", 0)}
              </text>
            </g>
          ))}

          {xTicks.map(({ month, index }) => (
            <g>
              <line
                x1={PADDING_LEFT + index * barSlot}
                x2={PADDING_LEFT + index * barSlot}
                y1={PADDING_TOP}
                y2={CHART_HEIGHT - PADDING_BOTTOM}
                stroke="#f5f5f5"
              />
              <text
                x={PADDING_LEFT + index * barSlot}
                y={CHART_HEIGHT - PADDING_BOTTOM + 16}
                text-anchor="middle"
                font-size="11"
                fill="#666"
              >
                {month.slice(0, 4)}
              </text>
            </g>
          ))}

          {/* Net revenue bar, with the refunded amount as a toggleable slice
              on top — together they reach the month's gross. */}
          {months.map((month, index) => {
            const net = month.revenue - month.refunded;
            const barX = PADDING_LEFT + index * barSlot;
            const barWidth = Math.max(1, barSlot - 1);
            const hovered = index === hoverIndex;
            return (
              <g>
                <rect
                  x={barX}
                  y={yFor(net)}
                  width={barWidth}
                  height={PADDING_TOP + plotHeight - yFor(net)}
                  fill={hovered ? BAR_HOVER_COLOR : BAR_COLOR}
                />
                {this.state.showRefunded && month.refunded > 0 && (
                  <rect
                    x={barX}
                    y={yFor(month.revenue)}
                    width={barWidth}
                    height={yFor(net) - yFor(month.revenue)}
                    fill={hovered ? REFUND_HOVER_COLOR : REFUND_COLOR}
                  />
                )}
              </g>
            );
          })}
        </svg>

        <label class="refundsToggle">
          <input
            type="checkbox"
            checked={this.state.showRefunded}
            onClick={() =>
              this.setState({ showRefunded: !this.state.showRefunded })
            }
          />
          <span class="refundSwatch"></span>
          Show refunds (slice on top of the net-revenue bar)
        </label>

        {hoverMonth ? (
          this.renderHoverDetail(hoverMonth)
        ) : (
          <p class="secondaryText">
            Hover over the chart for a monthly breakdown by plan.
          </p>
        )}
      </div>
    );
  }

  render() {
    return (
      <div class="adminRevenuePage">
        <h1>
          <a href="/admin">Admin</a> - Revenue
        </h1>

        {this.state.error && <p class="error">{this.state.error}</p>}

        {this.state.loading ? <p>Loading...</p> : this.renderChart()}
      </div>
    );
  }
}
